import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import "./login.css";
import "./login-tractor.css";

const FOTOS = [
  { f: "f1", k: "AMANECER", t: "Cada jornada empieza en el campo." },
  { f: "f2", k: "EN EL LOTE", t: "Protección que llega a cada hectárea." },
  { f: "f3", k: "BROTES NUEVOS", t: "Lo que se cuida, crece." },
  { f: "f4", k: "PRIMERAS LUCES", t: "Surcos parejos, cuentas claras." },
  { f: "f5", k: "DESDE ARRIBA", t: "Acompañamos cada zafra." },
];
const WEBP = typeof document !== "undefined" && document.createElement("canvas").toDataURL("image/webp").startsWith("data:image/webp");
const src = (f) => `/login/${f}.${WEBP ? "webp" : "jpg"}`;

const REDUCIDO = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const espera = (ms) => new Promise((r) => setTimeout(r, ms));

const Tractor = ({ className, style }) => (
  <svg viewBox="0 0 46 34" className={className} style={style} aria-hidden="true">
    <g className="cuerpo">
      <rect x="34" y="6" width="2.2" height="9" rx="1" fill="#4A3628" /><rect x="18" y="14" width="24" height="10" rx="2.5" fill="#007336" /><rect x="40" y="15.5" width="3.4" height="7" rx="1" fill="#00592A" />
      <rect x="22" y="12" width="13" height="3" rx="1.5" fill="#2E9A5E" /><path d="M3 22a10 10 0 0 1 20 0z" fill="#00592A" /><rect x="9.5" y="8" width="7.5" height="8.5" rx="2.5" fill="#5FA8D9" />
      <path d="M16 11.5l5 2.2" stroke="#5FA8D9" strokeWidth="2.2" strokeLinecap="round" /><path d="M19.5 9.6l2.4 5" stroke="#3B2E24" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="13.3" cy="5.6" r="3" fill="#E8B98E" /><ellipse cx="13.3" cy="3.6" rx="5.6" ry="1.3" fill="#E3C27A" /><rect x="10.8" y=".6" width="5" height="3.2" rx="1.6" fill="#E3C27A" /><rect x="10.8" y="2.6" width="5" height=".9" fill="#B88A3E" />
    </g>
    <g className="rueda"><circle cx="13" cy="24.5" r="9" fill="#2B2018" /><circle cx="13" cy="24.5" r="4" fill="#D9B65C" /><path d="M13 16v17M4.5 24.5h17" stroke="#3E3026" strokeWidth="1.4" /><circle cx="13" cy="24.5" r="1.4" fill="#8B5E3C" /></g>
    <g className="rueda"><circle cx="37" cy="28.5" r="5" fill="#2B2018" /><circle cx="37" cy="28.5" r="2.2" fill="#D9B65C" /><path d="M37 23.5v10M32 28.5h10" stroke="#3E3026" strokeWidth="1.1" /></g>
  </svg>
);

const anchoTexto = (txt, password) => {
  if (password) return txt.length * 9.6;
  const c = anchoTexto.c || (anchoTexto.c = document.createElement("canvas").getContext("2d"));
  c.font = "600 15px Urbanist, sans-serif";
  return c.measureText(txt).width;
};

const Campo = ({ id, label, value, onChange, type, placeholder, testid, extra, disabled, autoComplete, password }) => {
  const [foco, setFoco] = useState(false);
  const [anda, setAnda] = useState(false);
  const [ancho, setAncho] = useState(0);
  const t = useRef(null), esp = useRef(null);
  useEffect(() => { setAncho(password ? (value || "").length * 9.6 : (esp.current ? esp.current.offsetWidth : anchoTexto(value || "", false))); }, [value, password]);
  const w = Math.min(290, 18 + ancho + 4);
  const tipear = (e) => { onChange(e); setAnda(true); clearTimeout(t.current); t.current = setTimeout(() => setAnda(false), 380); };
  return (
    <div>
      <label htmlFor={id} className="tr-lab">{label}</label>
      <div className="tr-campo">
        <span ref={esp} aria-hidden="true" style={{ position: "absolute", visibility: "hidden", whiteSpace: "pre", font: "600 15px Urbanist, sans-serif" }}>{value}</span>
        <input id={id} type={type} value={value} onChange={tipear} onFocus={() => setFoco(true)} onBlur={() => setFoco(false)} placeholder={placeholder} data-testid={testid} required disabled={disabled} autoComplete={autoComplete} />
        {!REDUCIDO && (
          <div className={`tr-tractor ${foco ? "on" : ""} ${anda ? "anda" : ""}`} style={{ left: w }} data-testid={`${testid}-tractor`}>
            <Tractor /><i className="humo" /><i className="humo b" />
          </div>
        )}
        {!REDUCIDO && <div className={`tr-surco ${foco && value ? "on" : ""}`} style={{ width: Math.max(0, w - 18 + 6) }} />}
        {extra}
      </div>
    </div>
  );
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [ver, setVer] = useState(false);
  const [recordar, setRecordar] = useState(true);
  const [fase, setFase] = useState("quieto");   // quieto | viaje | entra | cerrado | llegada
  const [ayuda, setAyuda] = useState("El tractor te lleva hasta tu cartera");
  const [foto, setFoto] = useState(0);
  const [cargadas, setCargadas] = useState([0]);
  const [intro, setIntro] = useState(!REDUCIDO);
  const vivo = useRef(true);
  useEffect(() => {
    const t = setInterval(() => setFoto((f) => (f + 1) % FOTOS.length), 7000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { setCargadas((c) => (c.includes(foto) ? c : [...c, foto])); }, [foto]);
  useEffect(() => {
    if (!intro) return undefined;
    const fin = () => setIntro(false);
    const t = setTimeout(fin, 2500);
    window.addEventListener("keydown", fin); window.addEventListener("pointerdown", fin);
    return () => { clearTimeout(t); window.removeEventListener("keydown", fin); window.removeEventListener("pointerdown", fin); };
  }, [intro]);
  useEffect(() => {
    vivo.current = true;
    const h = document.documentElement, oscuro = h.classList.contains("tm-oscuro");
    h.classList.remove("tm-oscuro");   // el login siempre es claro; se restaura al salir
    return () => { vivo.current = false; if (oscuro) h.classList.add("tm-oscuro"); };
  }, []);

  const go = (m) => { setMode(m); setError(""); setInfo(""); };

  const viajar = async (peticion) => {
    // animación en paralelo con la petición; si falla, el tractor vuelve y se muestra el error
    setFase("viaje"); setAyuda("Rumbo al galpón…");
    const [res] = await Promise.all([peticion.then((d) => ({ ok: true, d })).catch((e) => ({ ok: false, e })), espera(1950)]);
    if (!res.ok) { setFase("quieto"); setAyuda("El tractor te lleva hasta tu cartera"); throw res.e; }
    setFase("entra"); await espera(400);
    setFase("cerrado"); setAyuda("Cerrando la tranquera…"); await espera(650);
    setFase("llegada");
    return res.d;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);
    try {
      if (mode === "login" || mode === "register") {
        const path = mode === "login" ? "/auth/login" : "/auth/register";
        const body = mode === "login" ? { email, password } : { email, password, name };
        const pet = api.post(path, body).then((r) => r.data);
        const data = REDUCIDO || mode !== "login" ? await pet : await viajar(pet);
        login(data);
        if (!REDUCIDO && mode === "login") await espera(700);
        navigate("/");
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email });
        setInfo(data.message || "Si el correo está registrado, te enviamos un código.");
        setMode("reset");
      } else if (mode === "reset") {
        const { data } = await api.post("/auth/reset-password", { email, code, new_password: newPassword });
        setInfo(data.message || "Contraseña actualizada.");
        setPassword(""); setCode(""); setNewPassword("");
        setMode("login");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      if (vivo.current) setLoading(false);
    }
  };

  const titulo = { login: "Bienvenido de nuevo", register: "Crear cuenta", forgot: "Recuperar acceso", reset: "Nueva contraseña" }[mode];
  const sub = { login: "Ingresá con tu usuario de la cartera", register: "Registrá tu cuenta corporativa", forgot: "Ingresá tu correo y te enviamos un código de verificación", reset: "Ingresá el código recibido y tu nueva contraseña" }[mode];
  const btnLabel = { login: "INGRESAR", register: "REGISTRARME", forgot: "ENVIAR CÓDIGO", reset: "RESTABLECER" }[mode];
  const enViaje = fase !== "quieto";
  const mostrar = (campo) => <button type="button" className="tr-mostrar" onClick={() => setVer((v) => !v)} data-testid={campo === "password" ? "login-mostrar-password" : undefined}>{ver ? "Ocultar" : "Mostrar"}</button>;

  return (
    <div className="lg" data-testid="login-page">
      <link rel="preload" as="image" href={src("f1")} />
      <div className="lg-fotos" aria-hidden="true">
        {FOTOS.map((p, i) => <div key={p.f} className={`lg-foto ${i === foto ? "on" : ""}`} style={cargadas.includes(i) ? { backgroundImage: `url(${src(p.f)})` } : undefined} />)}
      </div>
      <div className="lg-velo" aria-hidden="true" />
      <div className="lg-marca"><Logo variant="light" size="md" /><span>Gestión de cartera</span></div>
      {!intro && <div className="lg-rotulo" key={foto} data-testid="login-rotulo"><small>{FOTOS[foto].k}</small><p>{FOTOS[foto].t}</p></div>}
      <div className="lg-puntos" role="tablist" aria-label="Fotos">
        {FOTOS.map((p, i) => <button key={p.f} type="button" className={i === foto ? "on" : ""} onClick={() => setFoto(i)} aria-label={p.k} data-testid={`login-foto-${i + 1}`} />)}
      </div>
      <div className="lg-credito">Fotos: Unsplash</div>
      {!REDUCIDO && (
        <div className={`lg-noche ${intro ? "" : "fuera"}`} onClick={() => setIntro(false)} data-testid="login-intro" aria-hidden={!intro}>
          <button type="button" className="lg-sol" onClick={() => setIntro(false)} aria-label="Comenzar la jornada" data-testid="login-sol" />
          <p>Tocá el sol para comenzar la jornada</p>
        </div>
      )}

      <div className={`lg-tarjeta tr-card ${intro ? "oculta" : ""} ${fase === "llegada" ? "fuera" : ""}`} data-testid="login-tarjeta">
        <h1 data-testid="login-titulo">{titulo}</h1>
        <p className="tr-sub">{sub}</p>
        <form onSubmit={submit}>
          {mode === "register" && <Campo id="name" label="NOMBRE COMPLETO" value={name} onChange={(e) => setName(e.target.value)} type="text" placeholder="Nombre y apellido" testid="register-name-input" />}
          <Campo id="email" label="USUARIO" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="usuario@tecnomyl.com" testid="login-email-input" disabled={mode === "reset"} autoComplete="username" />
          {(mode === "login" || mode === "register") && (
            <Campo id="password" label="CONTRASEÑA" value={password} onChange={(e) => setPassword(e.target.value)} type={ver ? "text" : "password"} placeholder="••••••••" testid="login-password-input" autoComplete="current-password" password={!ver} extra={mostrar("password")} />
          )}
          {mode === "reset" && (
            <>
              <Campo id="code" label="CÓDIGO DE VERIFICACIÓN" value={code} onChange={(e) => setCode(e.target.value)} type="text" placeholder="6 dígitos" testid="reset-code-input" />
              <Campo id="newpass" label="NUEVA CONTRASEÑA" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} type={ver ? "text" : "password"} placeholder="Mínimo 6 caracteres" testid="reset-newpassword-input" password={!ver} extra={mostrar("new")} />
            </>
          )}
          {mode === "login" && (
            <div className="tr-fila">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}><input type="checkbox" checked={recordar} onChange={(e) => setRecordar(e.target.checked)} data-testid="login-recordarme" style={{ accentColor: "#007336" }} /> Recordarme</label>
              <button type="button" className="tr-link" data-testid="forgot-password-link" onClick={() => go("forgot")}>¿Olvidaste tu contraseña?</button>
            </div>
          )}
          {info && <div data-testid="login-info" className="tr-aviso info">{info}</div>}
          {error && <div data-testid="login-error" className="tr-aviso error">{error}</div>}
          <button type="submit" className={`tr-btn ${enViaje ? "viaje" : ""}`} data-testid="login-submit-button" disabled={loading}>
            <span className="et"><i />{loading && !enViaje && <Loader2 className="h-4 w-4 animate-spin" />}{btnLabel}</span>
            {!REDUCIDO && mode === "login" && (
              <>
                <span className="tr-pista" />
                <span className={`tr-galpon ${fase === "viaje" || fase === "entra" ? "abierto" : ""}`} data-testid="login-galpon"><i className="techo" /><i className="pared" /><i className="luz" /><i className="hoja i" /><i className="hoja d" /></span>
                <Tractor className={`tr-viajero ${fase === "entra" || fase === "cerrado" || fase === "llegada" ? "entra" : ""}`} style={enViaje ? { left: "calc(50% - 20px)" } : undefined} />
              </>
            )}
          </button>
          {mode === "login" && <p className="tr-ayuda" data-testid="login-ayuda">{ayuda}</p>}
        </form>
        <div className="tr-alt">
          {mode === "login" && <>¿No tenés cuenta? <button data-testid="switch-to-register" className="tr-link" onClick={() => go("register")}>Registrate</button></>}
          {mode === "register" && <>¿Ya tenés cuenta? <button data-testid="switch-to-login" className="tr-link" onClick={() => go("login")}>Iniciá sesión</button></>}
          {(mode === "forgot" || mode === "reset") && (
            <>
              <button data-testid="back-to-login" className="tr-link" onClick={() => go("login")}>Volver a iniciar sesión</button>
              {mode === "reset" && <>{" · "}<button data-testid="resend-code" className="tr-link" onClick={() => go("forgot")}>Reenviar código</button></>}
            </>
          )}
        </div>
      </div>

      <div className={`tr-llegada ${fase === "llegada" ? "on" : ""}`} data-testid="login-llegada" aria-live="polite">
        <div><h2>¡Llegaste al galpón!</h2><p>Cargando tu cartera del día…</p>{fase === "llegada" && <div className="tr-carga"><i /></div>}</div>
      </div>
    </div>
  );
}
